import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EnumJobStatus,
  JobStatus,
  JobType,
  skillsUsed,
} from "@utils/constants";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "@lib/dayjs";
import { timeSincePost } from "@utils/helperFunction";
import { publishUnpublish } from "@endpoints/job.service";
import { setJobDetail } from "@store/actions/job.action";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { ChevronDown } from "lucide-react";
import { RootState } from "@store/reducers";
import PublishIcon from "@assets/svg/PublishIcon";
import { getClassificationSummary } from "@utils/classification";
import JobDescription from "./components/JobDescription";
import SkillsAndExperience from "./components/SkillsAndExperience";
import Qualitfications from "./components/Qualitfications";

// const tabs = ["Job description", "Skills required"];

const tabs = [
  { name: "Job Description", path: "job-description" },
  { name: "Skills and Experience", path: "skills-and-experience" },
  { name: "Qualifications", path: "qualifications" },
];

const PublishDropdownInput = ({
  jobDetail,
  handlePublishUnpublish,
  publishLoading,
  colors,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const publishOptions = ["Publish", "Unpublish"];

  const currentSelection =
    jobDetail?.status === JobStatus.LIVE ? "Unpublish" : "Publish";

  const handleOptionSelect = (option) => {
    if (option !== currentSelection) {
      handlePublishUnpublish();
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center justify-between rounded-md border px-2 py-1.5 cursor-pointer bg-light-white-700`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="flex items-center gap-2">
          <PublishIcon className="h-5 w-5" />
          <input
            className="bg-transparent text-xs font-medium text-charcoal cursor-pointer outline-none"
            value={currentSelection}
            readOnly
          />
        </div>
        {publishLoading ? (
          <LoadingAnimation color={colors.White} className="w-4 h-4" />
        ) : (
          <ChevronDown fill={"white"} size={15} />
        )}
      </div>
      {isDropdownOpen && (
        <div
          className="absolute top-full left-0 w-full bg-white rounded-md border border-gray-300 z-50 shadow-lg max-h-52 overflow-y-auto"
          onMouseLeave={() => setIsDropdownOpen(false)}
        >
          {publishOptions.map((item, index) => (
            <div
              key={index}
              className={`px-4 py-2 cursor-pointer
                ${
                  item === currentSelection
                    ? "bg-gray-100 font-semibold"
                    : "hover:bg-gray-50"
                }
              `}
              onClick={() => handleOptionSelect(item)}
            >
              <span
                className={`
                text-xs 
                ${
                  item === currentSelection
                    ? "text-charcoal font-semibold"
                    : "text-gray-600"
                }
              `}
              >
                {item}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RenderBody = ({ activeTab, data }) => {
  switch (activeTab) {
    case "job-description":
      return <JobDescription data={data} />;
    case "skills-and-experience":
      return <SkillsAndExperience data={data} />;
    case "qualifications":
      return <Qualitfications data={data} />;
    default:
      break;
  }
};

export const JobDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { jobDetail } = useSelector((state: RootState) => state?.job);
  const [activeTab, setActiveTab] = useState("job-description");
  const [publishLoading, setPublishLoading] = useState(false);
  const [openJobCloseModal, setOpenJobCloseModal] = useState(false);
  const [openRefundModal, setOpenRefundModal] = useState(false);

  const job =
    JobType.find(({ value }) => value === jobDetail?.jobType)?.label || "-";

  const detailsColumn = [
    {
      title: "Location",
      value: jobDetail?.location,
    },
    {
      title: "Industry Type",
      value: jobDetail?.industryType,
    },
    {
      title: "Classification",
      value: getClassificationSummary(jobDetail?.classification),
    },
    {
      title: "Job Type",
      value: JobType?.find((item) => item?.value === jobDetail?.jobType)?.label,
    },
    {
      title: "Salary Range",
      value: `${jobDetail?.currency + jobDetail?.salary?.min} - ${
        jobDetail?.currency + jobDetail?.salary?.max
      }`,
    },
  ];

  const publishedColumn = [
    {
      title: "Views",
      value: "-",
    },
    {
      title: "Applicants",
      value: jobDetail?.applicantData?.length || 0,
    },
  ];

  const publishedColumn2 = [
    {
      title: "Published",
      value: timeSincePost(jobDetail?.publishedDate) || "N/A",
    },
    {
      title: "Expires",
      value: dayjs(jobDetail?.expiredAt)?.format("Do MMM YY hh:mm a"),
    },
    {
      title: "Edited",
      value: dayjs(jobDetail?.updatedAt)?.format("Do MMM YY hh:mm a"),
    },
  ];

  const handlePublishUnpublish = async () => {
    try {
      setPublishLoading(true);
      const jobStatus = jobDetail?.status === JobStatus.LIVE ? 2 : 1;
      const response = await publishUnpublish(jobDetail?._id, jobStatus);
      const { success, data } = response;
      if (success) {
        dispatch(
          setJobDetail({ ...jobDetail, status: jobStatus === 2 ? 5 : 2 }),
        );
        enqueueSnackbar(data?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar("An error occurred", {
        variant: "error",
      });
    } finally {
      setPublishLoading(false);
    }
  };

  return (
    <section className="flex flex-col flex-1 min-h-0 bg-white rounded-md border">
      {/* Basic Details */}
      <div className="flex justify-between p-7">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-lg text-surface-inverse">
            {jobDetail?.title}
          </span>
          <div className="flex items-center gap-3.5">
            <span className="text-xs font-medium text-light-black">
              Posted by{" "}
              <span className="text-xs font-semibold text-content">Agency</span>
            </span>
            <div className="h-3.5 w-px bg-gray-200" />
            <span className="text-xs font-medium text-light-black">
              {dayjs(jobDetail?.createdAt).fromNow()}
            </span>
            <div className="h-3.5 w-px bg-gray-200" />
            <span className="text-xs font-medium text-light-black">
              {dayjs(jobDetail?.createdAt).format("DD MMM YYYY")}
              &nbsp;•&nbsp;
              {dayjs(jobDetail?.createdAt).format("hh:mm a")}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span className="font-bold text-xsss tracking-widest uppercase text-light-black">
            Job Reference
          </span>
          <span className="font-bold text-xs text-charcoal">
            {jobDetail?.refNo}
          </span>
        </div>
      </div>
      {/* Tabs container */}
      <div className="flex items-center h-11 border-y">
        {tabs?.map((tab) => {
          const isActive = tab?.path === activeTab;
          return (
            <button
              key={tab?.path}
              onClick={() => setActiveTab(tab?.path)}
              className={`h-full flex flex-1 justify-center items-center text-xs font-semibold border-b-2 ${isActive ? "bg-white border-b-slate-600" : "bg-slate-100 text-slate-600 border-b-transparent"}`}
            >
              {tab?.name}
            </button>
          );
        })}
      </div>

      {/* Render Body */}
      <div className="flex flex-col p-7 min-h-0 flex-1">
        <RenderBody data={jobDetail} activeTab={activeTab} />
      </div>
    </section>
  );
};
